/**
 * **From the crew** — `docs/visit-page.md` §14, Board 10 A: photos, notes and
 * receipts, as they happened — a card per day, newest first, the day's chip
 * on it; the day's **close-out leads its day** (the lead's word in quotes,
 * *USED · EQUIPMENT HOURS · HOURS*); then everything else newest first: the
 * time, the face, *<name> <what>* (*2 photos · a receipt · a note and a
 * photo*), and the photos 150 × 100 with their tags and caption, the receipt
 * with its chip and **Review**, the note in the person's words (a voice note
 * with its transcript and a player).
 *
 * Toolbar: *All · 15 · Photos · 11 · Notes · 2 · Receipts · 2* (a close-out
 * counts as a note), *All days · Day 1 · Day 2 · Day 3*, *ⓘ Finished photos
 * come on the last day — Review & close asks for one*, **Upload**. A photo
 * opens the lightbox (Board 10 B, `photo-lightbox.tsx`); **Review** opens the
 * receipt on Money (`?tab=money&receipt=<id>`, lead round 1).
 *
 * Reads `GET /visits/:id/from-crew?kind=&day=` (`FromCrewRead`) — the server
 * filters, counts and orders; the page draws. A receipt's amount is internal
 * and absent for a role without *Costs & margins* (§2.3), so the line is then
 * the vendor alone. On a phone the same cards in one column, the photos two
 * across (§19.2), Upload being the green quick action.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  CloseOutEntry, CrewPhoto, FeedItem, FromCrewDay, FromCrewKind, FromCrewRead, PaidWith, PersonRef, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Segmented, StatusChip } from '../components/primitives.js'
import { useVisitTab } from './api.js'
import { dayText, hours1, hoursBare, money, timeIn } from './format.js'
import { Face } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { DayStateChip, PillChip } from './crew-parts.js'
import { PhotoLightbox } from './photo-lightbox.js'
import { useVisitUpload } from './visit-upload.js'
import { CLOSE_OUT_FOR_CREW, COMMON, FROM_CREW, RECEIPTS } from './copy.js'

const PAID_IN_SENTENCE: Partial<Record<PaidWith, string>> = RECEIPTS.paidWithInSentence

/** Every photo in the read, in the feed's order. */
/** The day selector's values: `all`, or a day's index. */
const ALL_DAYS = 'all'
const dayValue = (key: number | null): string => (key === null ? ALL_DAYS : String(key))

function photosOf(read: FromCrewRead): CrewPhoto[] {
  return read.groups.flatMap((group) => group.items.flatMap((item) => (item.kind === 'receipt' ? [] : item.photos)))
}

export function FromCrewTab({ visitId, read, phone }: { visitId: string; read: VisitRead; phone: boolean }) {
  const [kind, setKind] = useState<FromCrewKind>('all')
  const [day, setDay] = useState<number | null>(null)
  const { data, refresh } = useVisitTab<FromCrewRead>(visitId, '/from-crew', {
    kind: kind === 'all' ? undefined : kind,
    day: day ?? undefined,
  })
  const [opened, setOpened] = useState<string | null>(null)
  const navigate = useNavigate()
  const upload = useVisitUpload(visitId, read.orgUnitId, (result) => {
    if (result.tab) refresh(result.tab as FromCrewRead)
  }, 'from-crew')
  const photos = useMemo(() => (data ? photosOf(data) : []), [data])

  if (!data) return <div />

  const kinds: { key: FromCrewKind; label: string }[] = [
    { key: 'all', label: FROM_CREW.chips.all(String(data.counts.all)) },
    { key: 'photos', label: FROM_CREW.chips.photos(String(data.counts.photos)) },
    { key: 'notes', label: FROM_CREW.chips.notes(String(data.counts.notes)) },
    { key: 'receipts', label: FROM_CREW.chips.receipts(String(data.counts.receipts)) },
  ]
  const days: { key: number | null; label: string }[] = [
    { key: null, label: FROM_CREW.allDays },
    ...data.days.map((one) => ({ key: one.index, label: FROM_CREW.day(String(one.index)) })),
  ]
  const review = (receiptId: string) =>
    navigate(`/visits/${encodeURIComponent(visitId)}?tab=money&receipt=${encodeURIComponent(receiptId)}`)

  const toolbar = phone ? (
    <div className="flex flex-col gap-2">
      <div className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4">
        {kinds.map((one) => (
          <PillChip key={one.key} look="phone" selected={kind === one.key} onClick={() => setKind(one.key)}>
            {one.label}
          </PillChip>
        ))}
      </div>
      <div className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4">
        {days.map((one) => (
          <PillChip key={one.key ?? 'all'} look="phone" selected={day === one.key} onClick={() => setDay(one.key)}>
            {one.label}
          </PillChip>
        ))}
      </div>
      <span className="flex items-center gap-1.5 text-[12px] text-text-muted">
        <VisitGlyph name="info" size={13} stroke={2} />
        {FROM_CREW.sub}
      </span>
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2.5">
      <div className="flex flex-wrap gap-1.5">
        {kinds.map((one) => (
          <PillChip key={one.key} selected={kind === one.key} onClick={() => setKind(one.key)}>{one.label}</PillChip>
        ))}
      </div>
      <Segmented
        size="toolbar"
        label={FROM_CREW.allDays}
        options={days.map((one) => ({ value: dayValue(one.key), label: one.label }))}
        value={dayValue(day)}
        onChange={(next) => setDay(next === ALL_DAYS ? null : Number(next))}
      />
      <span className="flex-1" />
      <span className="inline-flex items-center gap-1.5 text-[12px] text-text-muted">
        <VisitGlyph name="info" size={13} stroke={2} />
        {FROM_CREW.sub}
      </span>
      {data.canUpload && (
        <button
          type="button"
          onClick={() => upload.openPicker()}
          className="inline-flex h-[34px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-button-toolbar border border-line bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover"
        >
          <VisitGlyph name="upload" size={14.5} stroke={2} />
          {FROM_CREW.upload}
        </button>
      )}
    </div>
  )

  return (
    <div data-as-drawn="vp-10 A" className={cn('flex flex-col', phone ? 'gap-3' : 'gap-4')}>
      {upload.input}
      {toolbar}
      {upload.error && <p className="m-0 text-[12px] text-danger">{upload.error}</p>}
      {data.groups.map((group) => (
        <DayCard
          key={group.day.visitId}
          group={group}
          read={read}
          phone={phone}
          onPhoto={(id) => setOpened(id)}
          onReview={review}
        />
      ))}
      {opened && (
        <PhotoLightbox
          visitId={visitId}
          read={read}
          photos={photos}
          openId={opened}
          customerFirstName={data.customerFirstName}
          onClose={() => setOpened(null)}
          onWritten={(result) => { if (result.tab) refresh(result.tab as FromCrewRead) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A day, and what came in on it
// ---------------------------------------------------------------------------

function DayCard({
  group, read, phone, onPhoto, onReview,
}: {
  group: FromCrewDay
  read: VisitRead
  phone: boolean
  onPhoto: (id: string) => void
  onReview: (receiptId: string) => void
}) {
  const date = dayText(group.day.date, 'date')
  const relative = group.day.relative === 'today' ? COMMON.today : group.day.relative === 'tomorrow' ? COMMON.tomorrow : null
  return (
    <div className={cn('rounded-record border border-line bg-surface', phone ? 'px-3.5 pb-1.5 pt-1' : 'px-[18px] pb-1.5 pt-1')}>
      <div className="flex items-center gap-2 py-2.5">
        <b className="text-[14.5px] font-bold text-text">{FROM_CREW.day(String(group.day.index))}</b>
        <span className="text-[12.5px] text-text-muted">{relative ? FROM_CREW.dayDate(date, relative) : date}</span>
        <span className="flex-1" />
        <DayStateChip chip={group.chip} />
      </div>
      {group.closeOut && <CloseOutRow entry={group.closeOut} read={read} phone={phone} />}
      {group.items.map((item) => (
        <ItemRow key={item.id} item={item} read={read} phone={phone} onPhoto={onPhoto} onReview={onReview} />
      ))}
    </div>
  )
}

/** The time, the face, the name and what — and, on a phone, the content under them. */
function Row({
  at, by, what, right, phone, read, children,
}: {
  at: string
  by: PersonRef
  what: string
  right?: ReactNode
  phone: boolean
  read: VisitRead
  children: ReactNode
}) {
  const time = timeIn(read.timeZone, at, 'ampm')
  if (phone) {
    return (
      <div className="flex flex-col gap-2 border-t border-line-faint py-3">
        <div className="flex items-center gap-2">
          <Face person={by} size={28} />
          <span className="flex min-w-0 flex-col">
            <span className="flex items-baseline gap-1.5">
              <b className="truncate text-[14px] font-bold text-text">{by.name}</b>
              <span className="truncate text-[12px] text-text-muted">{what}</span>
            </span>
            <span className="text-[12px] font-semibold text-text-body">{time}</span>
          </span>
          <span className="flex-1" />
          {right}
        </div>
        {children}
      </div>
    )
  }
  return (
    <div className="flex gap-3 border-t border-line-faint py-3">
      <div className="w-16 shrink-0"><b className="whitespace-nowrap text-[12.5px] font-bold text-text">{time}</b></div>
      <Face person={by} size={28} />
      <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <div className="flex items-center gap-1.5">
          <b className="text-[13px] font-bold text-text">{by.name}</b>
          <span className="text-[12px] text-text-muted">{what}</span>
          <span className="flex-1" />
          {right}
        </div>
        {children}
      </div>
    </div>
  )
}

/** A crew photo, 150 × 100 with its tag (Board 10); two across on a phone. */
function PhotoTile({ photo, phone, onOpen }: { photo: CrewPhoto; phone: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn('relative block shrink-0 overflow-hidden rounded-[10px] bg-surface-group',
        phone ? 'aspect-[3/2] w-full' : 'h-[100px] w-[150px]')}
    >
      <img src={photo.file.url} alt={photo.note ?? ''} className="h-full w-full object-cover" />
      {photo.tag && (
        <span className="absolute left-1.5 top-1.5 inline-flex h-[17px] items-center rounded-[5px] bg-surface/[.92] px-1.5 text-[10px] font-bold text-text">
          {FROM_CREW.tag[photo.tag]}
        </span>
      )}
    </button>
  )
}

function Photos({ photos, phone, onPhoto }: { photos: CrewPhoto[]; phone: boolean; onPhoto: (id: string) => void }) {
  return (
    <div className={cn(phone ? 'grid grid-cols-2 gap-2' : 'flex items-center gap-2')}>
      {photos.map((photo) => (
        <PhotoTile key={photo.id} photo={photo} phone={phone} onOpen={() => onPhoto(photo.id)} />
      ))}
    </div>
  )
}

function ItemRow({
  item, read, phone, onPhoto, onReview,
}: {
  item: FeedItem
  read: VisitRead
  phone: boolean
  onPhoto: (id: string) => void
  onReview: (receiptId: string) => void
}) {
  switch (item.kind) {
    case 'photos': {
      const what = item.photos.length === 1 ? FROM_CREW.aPhoto : FROM_CREW.photos(String(item.photos.length))
      return (
        <Row at={item.at} by={item.by} what={what} phone={phone} read={read}>
          {phone ? (
            <>
              <Photos photos={item.photos} phone onPhoto={onPhoto} />
              {item.caption && <span className="text-[13px] text-text-body">{item.caption}</span>}
            </>
          ) : (
            <div className="flex items-end gap-2">
              <Photos photos={item.photos} phone={false} onPhoto={onPhoto} />
              {item.caption && <span className="self-end text-[12.5px] text-text-body">{item.caption}</span>}
            </div>
          )}
        </Row>
      )
    }
    case 'note': {
      const what = item.photos.length > 0 ? FROM_CREW.aNoteAndAPhoto : FROM_CREW.aNote
      const words = (
        <span className={cn('max-w-[520px] leading-[1.5] text-text', phone ? 'text-[14px]' : 'text-[13px]')}>{item.text}</span>
      )
      return (
        <Row at={item.at} by={item.by} what={what} phone={phone} read={read}>
          <div className={cn('flex gap-3', phone ? 'flex-col' : 'items-start')}>
            {item.photos.length > 0 && <Photos photos={item.photos} phone={phone} onPhoto={onPhoto} />}
            <span className="flex flex-col gap-1.5">
              {words}
              {/* A voice note: the transcript is the note; the recording plays beside it (§14). */}
              {item.voice && <audio controls preload="none" src={item.voice.file.url} className="h-8 max-w-full" />}
            </span>
          </div>
        </Row>
      )
    }
    case 'receipt': {
      const words = RECEIPTS.state[item.state]
      const right = (
        <span className="flex items-center gap-1.5">
          <StatusChip group="receipt" status={item.state}>{words}</StatusChip>
          {item.state === 'to_review' && (
            <button
              type="button"
              onClick={() => onReview(item.id)}
              className={cn('inline-flex items-center justify-center whitespace-nowrap rounded-button border border-line bg-surface px-3.5',
                'text-[12px] font-semibold text-text hover:bg-hover', phone ? 'h-11' : 'h-7')}
            >
              {FROM_CREW.review}
            </button>
          )}
        </span>
      )
      const paid = PAID_IN_SENTENCE[item.paidWith]
      return (
        <Row at={item.at} by={item.by} what={FROM_CREW.aReceipt} right={right} phone={phone} read={read}>
          <div className="flex items-center gap-2.5">
            <span className="relative block h-[72px] w-14 shrink-0 overflow-hidden rounded-button bg-surface-group">
              {item.photo && <img src={item.photo.url} alt="" className="h-full w-full object-cover" />}
            </span>
            <span className="flex min-w-0 flex-col">
              <b className="text-[13px] font-bold text-text">
                {item.internal ? COMMON.dot(item.vendor, money(item.internal.amount)) : item.vendor}
              </b>
              <span className="text-[12px] text-text-muted">
                {paid ? FROM_CREW.receiptLine(item.description, paid) : item.description}
              </span>
            </span>
          </div>
        </Row>
      )
    }
  }
}

/** The day's close-out — it leads its day (§14). */
function CloseOutRow({ entry, read, phone }: { entry: CloseOutEntry; read: VisitRead; phone: boolean }) {
  const used = entry.used.map((line) => COMMON.dot(line.names.name,
    COMMON.qty(String(line.qty), line.unit ? (line.qty === 1 ? line.unit.one : line.unit.many) : '').trim()))
  const hours = entry.equipmentHours
    .map((one) => COMMON.dot(one.unit?.tag ?? one.type.name, hours1(one.hours)))
    .reduce((line, next) => (line ? COMMON.dot(line, next) : next), '')
  return (
    <Row at={entry.at} by={entry.by} what={FROM_CREW.closedOutTheDay} phone={phone} read={read}>
      <div className="flex flex-col gap-2 rounded-[12px] border border-green-band-line bg-green-band-bg px-3.5 py-3">
        {entry.note && <div className="text-[13px] leading-[1.5] text-text">{FROM_CREW.quoted(entry.note)}</div>}
        {entry.enteredBy && (
          <div className="text-[12px] text-text-muted">{CLOSE_OUT_FOR_CREW.enteredBy(entry.enteredBy.name, entry.by.name)}</div>
        )}
        <div className={cn('flex text-[12px] text-text-body', phone ? 'flex-col gap-2.5' : 'gap-[22px]')}>
          {used.length > 0 && (
            <div className="flex flex-col gap-0.5">
              <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">{FROM_CREW.used}</div>
              {used.map((line) => <span key={line}>{line}</span>)}
            </div>
          )}
          {hours && (
            <div className="flex flex-col gap-0.5">
              <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">{FROM_CREW.equipmentHours}</div>
              <span>{hours}</span>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">{FROM_CREW.hours}</div>
            <span>{FROM_CREW.hoursLine(hours1(entry.hours.onVisit), hoursBare(entry.hours.planned))}</span>
          </div>
        </div>
      </div>
    </Row>
  )
}
